'use client'
import { Otp } from '@repo/shared/components'
import { useTranslate } from '@repo/shared/hooks'
import { useRouter } from '@repo/shared/i18n/navigation'
import {
  useAgentLoginByPhone,
  useAgentLoginByEmail,
  useAgentResendOtp,
  useAgentAuthDetails,
} from '@repo/services'
import { Button, Card, Col, Icon } from '@repo/ui/components'
import { useRef, useState } from 'react'
import { ILoginOtpProps } from './login.types'
import { normalizeMobile } from '@repo/ui/lib'
import { OTP_LENGTH } from '@repo/shared/variables/otp.var'
import { useErrorHandler } from '@repo/shared/hooks'
import { toast } from '@repo/ui/components'
import { Cookie } from '@repo/tools'

export const LoginOtp = (props: ILoginOtpProps) => {
  const { identifier, handleBack, otp, isEmail, redirectingUrl } = props
  const { t } = useTranslate()
  const { replace } = useRouter()
  const [otpCode, setOtpCode] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const loginByPhone = useAgentLoginByPhone()
  const loginByEmail = useAgentLoginByEmail()
  const resendOtpMutation = useAgentResendOtp()
  const authDetailsQuery = useAgentAuthDetails()
  const { errorHandler } = useErrorHandler()

  const submitRef = useRef(null)

  const getResendOtpPayload = () =>
    isEmail
      ? { email: identifier, otp }
      : { phone: normalizeMobile(identifier), otp }

  const handleResend = () => {
    resendOtpMutation.mutate(getResendOtpPayload(), {
      onSuccess: () => {
        toast.success({
          title: t('common_otp_resend'),
        })
      },
      onError: errorHandler,
    })
  }

  const handleOtpComplete = (code: string) => {
    setOtpCode(code)
  }

  const handleEdit = () => {
    handleBack()
  }

  const handleAuthDetailsAndRedirect = async (redirectUrl: string) => {
    try {
      const authDetails = await authDetailsQuery.refetch()
      const kyc = authDetails.data?.data?.details?.kyc
      const phone = authDetails.data?.data?.phone || ''
      const email = authDetails.data?.data?.email || ''
      const mobileOrEmail = phone || email

      // If KYC is null, set cookie and redirect to quick-kyc
      if (!kyc) {
        Cookie.set('kyc_required', 'true', {
          path: '/',
        })
        if (mobileOrEmail) {
          Cookie.set('kyc_mobile_or_email', mobileOrEmail, {
            path: '/',
          })
        }
        const quickKycUrl = mobileOrEmail
          ? `/auth/quick-kyc?mobileOrEmail=${encodeURIComponent(mobileOrEmail)}`
          : '/auth/quick-kyc'
        if (typeof window !== 'undefined') {
          window.location.replace(quickKycUrl)
        } else {
          replace(quickKycUrl)
        }
        return
      }

      // Clear kyc_required cookie if KYC exists
      Cookie.remove('kyc_required')
      Cookie.remove('kyc_mobile_or_email')

      // Set KYC status cookie
      if (kyc.status) {
        Cookie.set('kyc_status', kyc.status, {
          path: '/',
        })
        Cookie.set('kyc_reason', kyc.comment || '', {
          path: '/',
        })
      }

      // Redirect based on KYC status
      let finalRedirectUrl = redirectUrl
      if (kyc.status === 'waiting_for_review') {
        finalRedirectUrl = '/auth/success'
      } else if (kyc.status === 'rejected') {
        finalRedirectUrl = '/auth/rejected'
      } else if (kyc.status === 'approved') {
        finalRedirectUrl = '/dashboard'
      }

      // Use window.location.replace to redirect and clear history
      if (typeof window !== 'undefined') {
        window.location.replace(finalRedirectUrl)
      } else {
        replace(finalRedirectUrl)
      }
    } catch {
      // If auth details fails, still redirect
      // Use window.location.replace to redirect and clear history
      if (typeof window !== 'undefined') {
        window.location.replace(redirectUrl)
      } else {
        replace(redirectUrl)
      }
    }
  }

  const handleSubmit = async (code: string = otpCode) => {
    if (!code || code.length < OTP_LENGTH) return

    setIsLoading(true)

    if (isEmail) {
      loginByEmail.mutate(
        {
          email: identifier,
          otp,
          code,
        },
        {
          onSuccess: (res) => {
            if (res.data?.accessToken && res.data?.refreshToken) {
              // Get auth details and set KYC status in cookie, then redirect based on KYC status
              handleAuthDetailsAndRedirect(redirectingUrl ?? '/dashboard')
            }
          },
          onError: (error) => {
            setIsLoading(false)
            errorHandler(error, t)
          },
        },
      )
    } else {
      loginByPhone.mutate(
        {
          phone: normalizeMobile(identifier),
          otp,
          code,
        },
        {
          onSuccess: (res) => {
            if (res.data?.accessToken && res.data?.refreshToken) {
              // Get auth details and set KYC status in cookie
              handleAuthDetailsAndRedirect(redirectingUrl ?? '/auth/quick-kyc')
            }
          },
          onError: (error) => {
            setIsLoading(false)
            errorHandler(error, t)
          },
        },
      )
    }
  }

  return (
    <Card className='p-xl rounded-sm'>
      <Col className='gap-3xl'>
        <Button
          startIcon={<Icon name='ArrowRightLine' />}
          variant='transparent'
          size='xs'
          onClick={handleBack}
          className='gap-2xs'
        >
          {t('common_back')}
        </Button>
        <Otp
          mobileOrEmail={identifier}
          onResend={handleResend}
          onComplete={handleOtpComplete}
          onEdit={handleEdit}
          focusRef={submitRef}
          resendLoading={resendOtpMutation.isPending}
          resendError={resendOtpMutation.isError}
        />
        <Button
          ref={submitRef}
          size='lg'
          className='w-full'
          onClick={() => handleSubmit()}
          disabled={otpCode.length < OTP_LENGTH}
          loading={
            isLoading ||
            (isEmail ? loginByEmail.isPending : loginByPhone.isPending)
          }
        >
          {t('common_login')}
        </Button>
      </Col>
    </Card>
  )
}
